import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { HavRegistration } from "./hooks/useHavData";

export type HavDecision = "approved" | "refused";

export interface HavTableRow {
  id: string;
  rowId: string;
  type: "principal" | "accompagnant";
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  sponsor_name: string;
  seat_category: string;
  seats_count: number;
  amount: number | null;
  additional_donation: number | null;
  payment_status: string;
  approval_status: string;
  principal_name: string | null;
  principalId: string;
}

interface HavTableProps {
  rows: HavTableRow[];
  decisions: Record<string, HavDecision | undefined>;
  onDecisionChange: (registrationId: string, decision: HavDecision) => void;
}

const stateBadge = (status?: string | null) => {
  if (status === "approved")
    return <span className="px-1.5 py-0.5 rounded text-[10px] bg-[#00123A] text-white">Validé</span>;
  if (status === "refused")
    return <span className="px-1.5 py-0.5 rounded text-[10px] bg-destructive text-destructive-foreground">Refusé</span>;
  return <span className="text-[10px] text-muted-foreground">En attente</span>;
};

const catLabel = (c?: string | null) =>
  c === "orchestre" ? "Orchestre" : c === "balcon" ? "Balcon" : "-";

export const HavTable = ({ rows, decisions, onDecisionChange }: HavTableProps) => {
  const DecisionCell = ({
    id,
    readOnly,
    disabled,
  }: {
    id: string;
    readOnly?: boolean;
    disabled?: boolean;
  }) => {
    const current = decisions[id];
    const base =
      "px-1.5 py-0.5 rounded text-[10px] border transition-colors whitespace-nowrap";
    const btn = (value: HavDecision, label: string) => {
      const active = current === value;
      return (
        <button
          type="button"
          key={value}
          disabled={disabled}
          onClick={() => !readOnly && !disabled && onDecisionChange(id, value)}
          className={`${base} ${
            active
              ? "bg-[#00123A] text-white border-[#00123A]"
              : "bg-background text-muted-foreground border-border hover:border-[#00123A]"
          } ${readOnly || disabled ? "cursor-default" : "cursor-pointer"} ${
            disabled ? "opacity-50" : ""
          }`}
        >
          {label}
        </button>
      );
    };
    return (
      <div className="flex gap-1 whitespace-nowrap">
        {btn("approved", "Validation")}
        {btn("refused", "Refus")}
      </div>
    );
  };

  return (
    <>
      <p className="mb-2 text-[10px] text-muted-foreground md:hidden">
        Faites défiler le tableau horizontalement pour voir toutes les colonnes.
      </p>
      <div
        className="rounded-md border overflow-x-auto max-w-full"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <Table className="text-[11px] w-full min-w-[1160px] table-fixed">

        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-[52px] px-1 py-1.5 h-7">État</TableHead>
            <TableHead className="w-[120px] px-1 py-1.5 h-7">Décision</TableHead>
            <TableHead className="w-[68px] px-1 py-1.5 h-7">Type</TableHead>
            <TableHead className="px-1 py-1.5 h-7">Nom</TableHead>
            <TableHead className="px-1 py-1.5 h-7">Prénom</TableHead>
            <TableHead className="px-1 py-1.5 h-7">Parrain</TableHead>
            <TableHead className="px-1 py-1.5 h-7">Participant principal</TableHead>
            <TableHead className="w-[68px] px-1 py-1.5 h-7 text-right">Montant</TableHead>
            <TableHead className="w-[68px] px-1 py-1.5 h-7 text-right">Don suppl.</TableHead>
            <TableHead className="w-[70px] px-1 py-1.5 h-7">Paiement</TableHead>
            <TableHead className="w-[48px] px-1 py-1.5 h-7 text-right">Places</TableHead>
            <TableHead className="w-[58px] px-1 py-1.5 h-7">Cat.</TableHead>
            <TableHead className="px-1 py-1.5 h-7">Téléphone</TableHead>
            <TableHead className="px-1 py-1.5 h-7">Email</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const sent = row.approval_status === "approved" || row.approval_status === "refused";
            const isGuest = row.type === "accompagnant";
            return (
              <TableRow
                key={row.rowId}
                className={`${isGuest ? "bg-muted/40" : "font-medium"} hover:bg-muted/60`}
              >
                <TableCell className="px-1 py-1">{stateBadge(row.approval_status)}</TableCell>
                <TableCell className="px-1 py-1">
                  <DecisionCell id={row.principalId} readOnly={isGuest} disabled={sent} />
                </TableCell>
                <TableCell className="px-1 py-1">
                  {isGuest ? "Accomp." : "Principal"}
                </TableCell>
                <TableCell className="px-1 py-1 truncate" title={row.last_name}>
                  {row.last_name}
                </TableCell>
                <TableCell className="px-1 py-1 truncate" title={row.first_name}>
                  {row.first_name}
                </TableCell>
                <TableCell className="px-1 py-1 truncate" title={row.sponsor_name || undefined}>
                  {row.sponsor_name || "-"}
                </TableCell>
                <TableCell className="px-1 py-1 truncate" title={row.principal_name || undefined}>
                  {row.principal_name || "-"}
                </TableCell>
                <TableCell className="px-1 py-1 text-right">
                  {row.amount !== null ? `${row.amount.toLocaleString("fr-FR")} €` : "-"}
                </TableCell>
                <TableCell className="px-1 py-1 text-right">
                  {row.additional_donation !== null
                    ? `${row.additional_donation.toLocaleString("fr-FR")} €`
                    : "-"}
                </TableCell>
                <TableCell className="px-1 py-1 truncate" title={row.payment_status}>
                  {row.payment_status}
                </TableCell>
                <TableCell className="px-1 py-1 text-right">{row.seats_count}</TableCell>
                <TableCell className="px-1 py-1">{catLabel(row.seat_category)}</TableCell>
                <TableCell className="px-1 py-1 truncate" title={row.phone || undefined}>
                  {row.phone || "-"}
                </TableCell>
                <TableCell className="px-1 py-1 truncate" title={row.email}>
                  {row.email}
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={14} className="text-center text-muted-foreground py-6 text-xs">
                Aucune préinscription
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </div>
    </>
  );

};

export const flattenHavRegistrations = (
  registrations: HavRegistration[]
): HavTableRow[] => {
  const rows: HavTableRow[] = [];
  for (const r of registrations) {
    rows.push({
      id: r.id,
      rowId: r.id,
      type: "principal",
      first_name: r.first_name,
      last_name: r.last_name,
      email: r.email,
      phone: r.phone,
      sponsor_name: r.sponsor_name,
      seat_category: r.seat_category,
      seats_count: r.seats_count,
      amount: r.amount,
      additional_donation: r.additional_donation ?? 0,
      payment_status: r.payment_status,
      approval_status: r.approval_status,
      principal_name: null,
      principalId: r.id,
    });

    for (let i = 0; i < (r.guests ?? []).length; i++) {
      const g = r.guests![i];
      rows.push({
        id: g.id ?? `${r.id}-guest-${i}`,
        rowId: `${r.id}-guest-${i}`,
        type: "accompagnant",
        first_name: g.first_name,
        last_name: g.last_name,
        email: g.email ?? "-",
        phone: g.phone ?? null,
        sponsor_name: r.sponsor_name,
        seat_category: g.seat_category ?? r.seat_category,
        seats_count: 1,
        amount: null,
        additional_donation: null,
        payment_status: r.payment_status,
        approval_status: r.approval_status,
        principal_name: `${r.first_name} ${r.last_name}`,
        principalId: r.id,
      });
    }
  }
  return rows;
};
